/**
 * PushHardGate — a GENTLE, dismissible notification nudge.
 *
 * History: originally a non-dismissable full-screen blocker ("force
 * these on", 2026-02-27). User feedback 2026-06 — "I absolutely HATE
 * the turn-on-notifications popup … do we really need it?" — so it was
 * downgraded from a full-screen gate to a slim top banner:
 *   • NEVER blocks the app. No fixed inset-0 overlay, no backdrop.
 *   • Renders a thin banner at the top with an Enable button + an X.
 *   • Tapping the X dismisses it PERMANENTLY on that device (no more
 *     daily re-nagging). Crew can still enable push anytime from
 *     Settings / the bell.
 *   • Two states: (a) iOS Safari not installed → "install" nudge;
 *     (b) push supported but not enabled → "enable" nudge.
 *   • Rechecks every 3s so it disappears the moment push is granted.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { BellRinging, DeviceMobile, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { enableWebPush, isIOS, isStandalonePWA, isPushSupported } from "../lib/webPush";
import InstallAppPrompt from "./InstallAppPrompt";

export default function PushHardGate({ crewId }) {
  // gateState: null (checking) | "ok" (nothing blocks) | "install-required" | "enable-required"
  const [gateState, setGateState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  // Permanent per-device dismissal. User feedback 2026-06 — the old
  // 24h bypass meant the popup came BACK every day, which they hated.
  // Now the X dismisses it for good on this device. Migrates the old
  // timestamp key automatically (any prior skip → treated as dismissed).
  const DISMISS_KEY = crewId ? `wowPushNudgeDismissed:${crewId}` : "";
  const LEGACY_KEY = crewId ? `wowPushGateInstallBypass:${crewId}` : "";
  const [dismissed, setDismissed] = useState(() => {
    if (!DISMISS_KEY) return false;
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return true;
      if (localStorage.getItem(LEGACY_KEY)) return true; // old skip → keep it dismissed
    } catch { /* quota */ }
    return false;
  });
  // Only attempt the silent (no-prompt) re-subscribe once per mount so
  // the 3s poll can't spam it.
  const silentTried = useRef(false);

  const check = useCallback(async () => {
    if (!crewId) { setGateState(null); return; }
    // Browsers that literally can't do push (e.g. legacy) — don't
    // block, just noop. This is the "nothing to enforce" branch.
    if (!isPushSupported() && !isIOS()) { setGateState("ok"); return; }
    if (isIOS() && !isStandalonePWA()) {
      setGateState("install-required");
      return;
    }
    const perm = (typeof Notification !== "undefined" && Notification.permission) ? Notification.permission : "default";
    // Permission already granted → NEVER block the app. If the server
    // subscription is missing (new device / fresh session), quietly
    // re-subscribe in the background — no prompt, no gate. Fixes the
    // "keeps nagging even though I already enabled notifications" case.
    if (perm === "granted") {
      setGateState("ok");
      if (!silentTried.current) {
        silentTried.current = true;
        // Re-subscribe once per mount. enableWebPush drops any existing
        // browser subscription and recreates it against the CURRENT VAPID
        // public key, so a stale subscription left over from an old key
        // (which 403s silently on the push service) self-heals on app
        // open instead of failing forever. Idempotent for healthy
        // devices (same endpoint, upserted server-side).
        enableWebPush(crewId).catch(() => {});
      }
      return;
    }
    // permission is "default" (never asked) or "denied" → show the gate.
    setGateState("enable-required");
  }, [crewId]);

  useEffect(() => {
    check();
    const t = setInterval(check, 3000);
    return () => clearInterval(t);
  }, [check]);

  const enable = useCallback(async () => {
    if (busy || !crewId) return;
    setBusy(true);
    try {
      const r = await enableWebPush(crewId);
      if (r?.ok) {
        toast.success("Notifications enabled — you're all set.");
        check();
      } else {
        toast.error(r?.error || "Couldn't enable. Check your browser's notification settings.");
      }
    } finally {
      setBusy(false);
    }
  }, [busy, crewId, check]);

  if (gateState === null || gateState === "ok") return null;
  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* quota */ }
    setDismissed(true);
    toast.message("Got it — we won't ask again. Turn notifications on anytime from Settings.");
  };

  const isInstall = gateState === "install-required";

  return (
    <>
      <div
        className="fixed top-2 inset-x-2 z-[9999] rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-white/10 flex items-center gap-3 px-3 py-2.5"
        role="status"
        data-testid="push-hard-gate"
      >
        <div className="w-9 h-9 rounded-full bg-rose-500/90 flex items-center justify-center flex-shrink-0">
          {isInstall
            ? <DeviceMobile weight="fill" className="w-5 h-5" />
            : <BellRinging weight="fill" className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black leading-tight" data-testid="push-hard-gate-title">
            {isInstall ? "Install for alerts" : "Turn on notifications"}
          </p>
          <p className="text-[11px] text-white/70 leading-snug truncate">
            {isInstall
              ? "Add to Home Screen to get walkie clips & task alerts."
              : "Get walkie clips, task alerts & shift updates."}
          </p>
        </div>
        {isInstall ? (
          <button
            onClick={() => setShowInstall(true)}
            className="flex-shrink-0 h-9 px-3 rounded-full bg-white text-slate-900 font-black text-[11px] uppercase tracking-wider hover:bg-slate-100"
            data-testid="push-hard-gate-install"
          >
            Install
          </button>
        ) : (
          <button
            onClick={enable}
            disabled={busy}
            className="flex-shrink-0 h-9 px-3 rounded-full bg-white text-slate-900 font-black text-[11px] uppercase tracking-wider hover:bg-slate-100 disabled:opacity-60 inline-flex items-center gap-1"
            data-testid="push-hard-gate-enable"
          >
            <BellRinging weight="fill" className="w-3.5 h-3.5" />
            {busy ? "…" : "Enable"}
          </button>
        )}
        <button
          onClick={dismiss}
          className="flex-shrink-0 w-8 h-8 rounded-full hover:bg-white/10 inline-flex items-center justify-center text-white/70 hover:text-white"
          aria-label="Dismiss"
          data-testid="push-hard-gate-skip"
        >
          <X weight="bold" className="w-4 h-4" />
        </button>
      </div>
      {showInstall && (
        <InstallAppPrompt variant="button" forceOpen onForceClose={() => { setShowInstall(false); check(); }} />
      )}
    </>
  );
}
